import { ColorProps, FlexProps } from '@chakra-ui/react';
import { Amount, Checkbox, Flex, Paragraph } from '../../../atoms';

type StyleVariantType = 'default' | 'done' | 'archived' | 'warning';
export type PayrollImpactCardVariantType = StyleVariantType | 'checkbox';

type VariantObjectReturnType = {
  backgroundColor: ColorProps['color'];
  borderColor: ColorProps['color'];
  dateTextColor: ColorProps['textColor'];
  textColor: ColorProps['textColor'];
};

type Props = {
  title: string;
  subtitle?: string;
  amount: number;
  variant?: PayrollImpactCardVariantType;
  checkBoxData?: {
    isChecked: boolean;
    onChange: () => void;
    isDisabled?: boolean;
  };
} & FlexProps;

const getCheckBoxStyle = (isChecked: boolean): VariantObjectReturnType => ({
  backgroundColor: isChecked ? 'palette_blue.5' : 'inherit',
  borderColor: isChecked ? 'palette_blue.100' : 'palette_gray.10',
  dateTextColor: 'palette_gray.100',
  textColor: 'palette_gray.100'
});

const variantStyle: {
  [type in StyleVariantType]: VariantObjectReturnType;
} = {
  done: {
    backgroundColor: 'palette_gray.0',
    borderColor: 'palette_gray.10',
    dateTextColor: 'palette_gray.100',
    textColor: 'palette_gray.100'
  },
  archived: {
    backgroundColor: 'inherit',
    borderColor: 'palette_gray.10',
    dateTextColor: 'palette_gray.60',
    textColor: 'palette_gray.60'
  },
  warning: {
    backgroundColor: 'palette_alert.0',
    borderColor: 'palette_alert.100',
    dateTextColor: 'palette_alert.100',
    textColor: 'palette_gray.100'
  },
  default: {
    backgroundColor: 'inherit',
    borderColor: 'palette_gray.10',
    dateTextColor: 'palette_gray.100',
    textColor: 'palette_gray.100'
  }
};

export function PayrollImpactCard({
  title,
  subtitle,
  variant,
  amount,
  checkBoxData,
  ...rest
}: Props): JSX.Element {
  const isCheckBoxDisabled = !!checkBoxData?.isDisabled;
  const hasCheckBox = variant === 'checkbox' && checkBoxData;
  const currentVariantStyle =
    variant === 'checkbox'
      ? getCheckBoxStyle(checkBoxData?.isChecked ?? false)
      : variantStyle[variant ?? 'default'];

  return (
    <Flex
      backgroundColor={currentVariantStyle.backgroundColor}
      borderWidth="1px"
      borderRadius="radii.x1"
      borderColor={currentVariantStyle.borderColor}
      p="space_unit.x2"
      w="100%"
      {...rest}
    >
      {hasCheckBox && (
        <Checkbox
          isDisabled={isCheckBoxDisabled}
          isChecked={checkBoxData.isChecked}
          onChange={checkBoxData.onChange}
          size="lg"
          mr="space_unit.x2"
        />
      )}
      <Flex w="100%" justifyContent="space-between" alignItems="center">
        <Flex flexDir="column">
          <Paragraph
            color={currentVariantStyle.textColor}
            textDecoration={variant === 'done' ? 'line-through' : 'none'}
            fontWeight="bold"
          >
            {title}
          </Paragraph>
          {subtitle && (
            <Paragraph color={currentVariantStyle.dateTextColor} size="sm">
              {subtitle}
            </Paragraph>
          )}
        </Flex>
        <Amount
          textDecoration={variant === 'done' ? 'line-through' : 'none'}
          amount={amount}
          showPosSign={false}
          amountTextProps={{
            fontWeight: 'bold',
            color: currentVariantStyle.textColor
          }}
          currencyTextProps={{
            color: currentVariantStyle.textColor
          }}
        />
      </Flex>
    </Flex>
  );
}
